import mixpanel, { type Dict } from 'mixpanel-browser';

/**
 * Thin wrapper around the mixpanel-browser SDK.
 *
 * All event emission in the app should go through this module so that:
 *   1. Nothing crashes when VITE_MIXPANEL_TOKEN is missing (local dev).
 *   2. Global properties (env, app version) can be augmented in one place.
 *   3. The SDK can be swapped out without touching call sites.
 *
 * Identity lifecycle:
 *   - Anonymous: the SDK auto-generates a distinct_id and stores it in a cookie.
 *   - On login: call `identifyUser()` to switch the distinct_id to the user UUID.
 *   - On logout: call `resetUser()` to start a fresh anonymous session.
 */

let initialized = false;

const getToken = (): string | undefined => {
   return import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
};

/**
 * Session replay sample rate (0-100). Defaults to 100% outside of
 * production so local/staging debugging always has a replay. In production
 * set `VITE_MIXPANEL_SESSION_REPLAY_PERCENT` to something cheaper (e.g. 10)
 * once you understand volume.
 */
const getSessionReplayPercent = (): number => {
   const raw = import.meta.env.VITE_MIXPANEL_SESSION_REPLAY_PERCENT as
      | string
      | undefined;
   if (raw != null && raw !== '') {
      const parsed = Number(raw);
      if (!Number.isNaN(parsed)) return Math.max(0, Math.min(100, parsed));
   }
   return import.meta.env.MODE === 'production' ? 10 : 100;
};

export const initAnalytics = (): void => {
   if (initialized) return;
   const token = getToken();
   if (!token) {
      // No token — run as a no-op. Avoids noisy console errors locally.
      return;
   }
   mixpanel.init(token, {
      // `identity_merge: true` lets anonymous events merge into the identified
      // user when identify() is called, preserving top-of-funnel behavior.
      // @ts-expect-error — option exists at runtime but not in old typings.
      identity_merge: true,
      persistence: 'localStorage',
      track_pageview: false,
      ignore_dnt: false,

      // ─── Autocapture (powers heatmaps + click paths) ──────────────────
      // Enables Mixpanel's automatic capture of clicks, scrolls, and page
      // views without explicit `track()` calls. This is what Heatmaps and
      // Flows reports consume. We disable `capture_text_content` so button
      // labels that may contain user content (e.g. "Book <hotel name>")
      // don't leak into Mixpanel properties as free text.
      // @ts-expect-error — option exists at runtime but not in old typings.
      autocapture: {
         pageview: false, // we emit page_view ourselves
         click: true,
         input: false,
         scroll: true,
         submit: false,
         capture_text_content: false,
      },

      // ─── Session Replay ────────────────────────────────────────────────
      // Records a video-like playback of the user's DOM. Everything inside
      // `input`, `textarea`, and `select` is masked by default. On top of
      // that we:
      //   - Block entire sections with class `mp-no-record` (payment form,
      //     card list — kept out of PCI scope entirely).
      //   - Mask text in elements with class `mp-mask` (personal details).
      // @ts-expect-error — option exists at runtime but not in old typings.
      record_sessions_percent: getSessionReplayPercent(),
      // @ts-expect-error — option exists at runtime but not in old typings.
      record_block_class: 'mp-no-record',
      // @ts-expect-error — option exists at runtime but not in old typings.
      record_mask_text_class: 'mp-mask',
      // @ts-expect-error — option exists at runtime but not in old typings.
      record_mask_text_selector: 'input, textarea, select',
      // Stop recording after 30 min of inactivity so idle tabs don't
      // silently burn replay minutes.
      // @ts-expect-error — option exists at runtime but not in old typings.
      record_idle_timeout_ms: 30 * 60 * 1000,
   });
   mixpanel.register({
      env: import.meta.env.MODE,
      appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
   });
   initialized = true;
};

export const isAnalyticsEnabled = (): boolean => initialized;

export const track = (event: string, properties: Dict = {}): void => {
   if (!initialized) return;
   try {
      mixpanel.track(event, properties);
   } catch (err) {
      // Never let analytics break the app.
      console.warn(`[analytics] track "${event}" failed`, err);
   }
};

export interface IdentifyPayload {
   userId: string;
   email?: string;
   firstName?: string;
   lastName?: string;
   phoneNumber?: string;
   createdAt?: string;
}

export const identifyUser = (payload: IdentifyPayload): void => {
   if (!initialized) return;
   try {
      mixpanel.identify(payload.userId);
      mixpanel.people.set({
         $email: payload.email,
         $first_name: payload.firstName,
         $last_name: payload.lastName,
         phoneNumber: payload.phoneNumber,
         createdAt: payload.createdAt,
      });
   } catch (err) {
      console.warn('[analytics] identifyUser failed', err);
   }
};

export const resetUser = (): void => {
   if (!initialized) return;
   try {
      mixpanel.reset();
   } catch (err) {
      console.warn('[analytics] resetUser failed', err);
   }
};

export const setOrganizationGroup = (organizationId: string): void => {
   if (!initialized || !organizationId) return;
   try {
      mixpanel.set_group('organization', organizationId);
   } catch (err) {
      console.warn('[analytics] setOrganizationGroup failed', err);
   }
};

/**
 * Register a set of super-properties. Super-properties are stored in the
 * SDK's persistence layer and automatically attached to every subsequent
 * event — no per-call-site wiring needed.
 *
 * `undefined`/`null` values are stripped so we don't clobber previously
 * registered props with empty ones.
 */
export const registerSuperProperties = (props: Record<string, unknown>): void => {
   if (!initialized) return;
   try {
      const clean: Record<string, unknown> = {};
      Object.entries(props).forEach(([k, v]) => {
         if (v !== undefined && v !== null && v !== '') clean[k] = v;
      });
      if (Object.keys(clean).length === 0) return;
      mixpanel.register(clean);
   } catch (err) {
      console.warn('[analytics] registerSuperProperties failed', err);
   }
};

export const setPeopleProperties = (props: Record<string, unknown>): void => {
   if (!initialized) return;
   try {
      const clean: Record<string, unknown> = {};
      Object.entries(props).forEach(([k, v]) => {
         if (v !== undefined && v !== null && v !== '') clean[k] = v;
      });
      if (Object.keys(clean).length === 0) return;
      mixpanel.people.set(clean);
   } catch (err) {
      console.warn('[analytics] setPeopleProperties failed', err);
   }
};

export const unregisterSuperProperty = (key: string): void => {
   if (!initialized) return;
   try {
      mixpanel.unregister(key);
   } catch (err) {
      console.warn('[analytics] unregisterSuperProperty failed', err);
   }
};

/**
 * Attach the current site (white-label tenant) to every future event.
 * Called once by SiteGuardProvider after `siteConfig` resolves.
 */
export interface SiteContext {
   id: string;
   code?: string;
   slug?: string;
   displayName?: string;
}

export const setSiteContext = (site: SiteContext | null | undefined): void => {
   if (!site?.id) return;
   registerSuperProperties({
      siteId: site.id,
      siteCode: site.code,
      siteSlug: site.slug,
      siteName: site.displayName,
   });
};

/**
 * Attach the current organization (nonprofit) to every future event.
 * Called whenever the active organization context changes — on user login,
 * when landing on an org microsite (anonymous), or when the user switches
 * orgs from the org selector.
 *
 * Also keeps Mixpanel's Group Analytics group key in sync so the same
 * events can be sliced either as a property or as a group.
 */
export interface OrganizationContext {
   id: string;
   slug?: string;
   name?: string;
}

export const setOrganizationContext = (
   org: OrganizationContext | null | undefined,
): void => {
   if (!org?.id) return;
   registerSuperProperties({
      organizationId: org.id,
      organizationSlug: org.slug,
      organizationName: org.name,
   });
   setOrganizationGroup(org.id);
};

export const clearOrganizationContext = (): void => {
   unregisterSuperProperty('organizationId');
   unregisterSuperProperty('organizationSlug');
   unregisterSuperProperty('organizationName');
};

/**
 * Read the current distinct_id. The frontend attaches this to API calls via
 * a custom header so the backend can emit events against the same identity.
 */
export const getDistinctId = (): string | null => {
   if (!initialized) return null;
   try {
      return mixpanel.get_distinct_id() || null;
   } catch {
      return null;
   }
};
